import { objectWithoutKeys, updateRecord } from '@ms/yammer-data-support';

import type { Publishers } from '../types';

import type { PublishersAction } from './reducer.types';
import type { Publisher } from './types';

const removePublisher = (state: Publishers, publisherId: Publisher['id']) => objectWithoutKeys(state, [publisherId]);

const collapsePublisher = (state: Publishers, publisherId: Publisher['id']): Publishers =>
  updateRecord(state, publisherId, { isExpanded: false });

const expandPublisher = (state: Publishers, publisherId: Publisher['id']): Publishers =>
  updateRecord(state, publisherId, { isExpanded: true });

const completePublish = (
  state: Publishers,
  publisherId: Publisher['id'],
  shouldKeepPublisherOpen: boolean | undefined
): Publishers => (shouldKeepPublisherOpen ? state : removePublisher(state, publisherId));

const completeEditPublish = (
  state: Publishers,
  payload: Extract<PublishersAction, { readonly type: 'PUBLISHER.PUBLISH_EDIT_MESSAGE_FULFILLED' }>['payload']
): Publishers =>
  completePublish(
    state,
    payload.publisherId,
    payload.keepPublisherOpen && payload.wasPublishedAsDraft && !payload.isScheduledPost
  );

type CloseActionsReducer = (state: Publishers, action: PublishersAction) => Publishers;
export const closeActionsReducer: CloseActionsReducer = (state, action) => {
  switch (action.type) {
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_PENDING':
      return updateRecord(state, action.payload.publisherId, {
        wasPublishedAsDraft: action.payload.isDraft,
      });
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_PENDING':
      return updateRecord(state, action.payload.publisherId, {
        wasPublishedAsDraft: action.payload.isDraft,
      });
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_PENDING':
      return updateRecord(state, action.payload.publisherId, {
        wasPublishedAsDraft: action.payload.isDraft,
      });
    case 'PUBLISHER.REMOVE':
      return removePublisher(state, action.payload.id);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_FULFILLED':
      return removePublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED':
      return removePublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED':
      return removePublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED':
      return removePublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED':
      return removePublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_FULFILLED':
      return removePublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_FULFILLED':
      return completeEditPublish(state, action.payload);
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_FULFILLED':
      return removePublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_FULFILLED':
      return removePublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED':
      return removePublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_TEAMS_MEETING_MESSAGE_FULFILLED':
      return removePublisher(state, action.payload.publisherId);
    case 'PUBLISHER.PUBLISH_NETWORK_QUESTION_MESSAGE_FULFILLED':
      return removePublisher(state, action.payload.publisherId);
    case 'PUBLISHER.NON_REDUX_PUBLISH_FULFILLED':
      return completePublish(state, action.payload.publisherId, action.payload.keepPublisherOpen);
    case 'PUBLISHER.COLLAPSE':
      return collapsePublisher(state, action.payload.publisherId);
    case 'PUBLISHER.EXPAND':
      return expandPublisher(state, action.payload.publisherId);
    default:
      return state;
  }
};
